/**
 * Usage Bar Web Component
 *
 * Displays a single usage bar for a bundle assignment with:
 * - Plan name (top-left)
 * - Renewal/Expiry date (top-right)
 * - Usage amount (bottom-left)
 * - Total/Unlimited (bottom-right)
 * - Progress bar
 *
 * Usage:
 * <usage-bar
 *   plan-name="United Kingdom Bolt-on - 5GB"
 *   used-amount="2.5"
 *   total-amount="5"
 *   unit="GB"
 *   unlimited="false"
 *   auto-renew="false"
 *   end-date="2025-03-31T23:59:59Z"
 *   bar-fill-color="#5b4db3"
 *   bar-bg-color="#d9d9d9"
 *   bar-height="10"
 *   bar-border-radius="5"
 *   card-bg-color="#FFFFFF"
 *   card-bg-transparent="false"
 *   card-padding="16"
 *   card-border-radius="8"
 *   card-border-width="0"
 *   card-border-type="solid"
 *   card-border-color="#E0E0E0"
 *   card-border-gradient-start="#5b4db3"
 *   card-border-gradient-end="#8b7dd8"
 *   card-border-gradient-angle="135"
 *   title-text-color="#000000"
 *   title-text-size="14"
 *   title-text-weight="600"
 *   title-text-opacity="100"
 *   expiry-text-color="#000000"
 *   expiry-text-size="12"
 *   expiry-text-weight="400"
 *   expiry-text-opacity="100"
 *   used-text-color="#000000"
 *   used-text-size="12"
 *   used-text-weight="500"
 *   used-text-opacity="100"
 *   used-amount-icon="data_usage"
 *   used-amount-icon-color="#000000"
 *   used-amount-icon-size="16"
 *   total-text-color="#000000"
 *   total-text-size="12"
 *   total-text-weight="400"
 *   total-text-opacity="100"
 *   label-used="Used"
 *   label-out-of="out of"
 *   label-renews="Renews"
 *   label-expires="Expires"
 *   label-unlimited="Unlimited data"
 * ></usage-bar>
 */

class UsageBar extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    this.render();
  }

  static get observedAttributes() {
    return [
      "plan-name",
      "used-amount",
      "total-amount",
      "unit",
      "unlimited",
      "auto-renew",
      "end-date",
      "start-date",
      "bundle-started",
      "renews-position",
      "date-format",
      "show-allowance-line",
      "allowance-line-color",
      "allowance-line-font-weight",
      "label-started",
      "label-on",
      "label-allowance-resets-in",
      "label-allowance-expires-in",
      "label-days",
      "bar-fill-color",
      "bar-fill-gradient-start",
      "bar-fill-gradient-end",
      "bar-bg-color",
      "bar-height",
      "bar-border-radius",
      "card-bg-color",
      "card-bg-transparent",
      "card-padding",
      "card-border-radius",
      "card-border-width",
      "card-border-type",
      "card-border-color",
      "card-border-gradient-start",
      "card-border-gradient-end",
      "card-border-gradient-angle",
      "title-text-color",
      "title-text-size",
      "title-text-weight",
      "title-text-opacity",
      "expiry-text-color",
      "expiry-text-size",
      "expiry-text-weight",
      "expiry-text-opacity",
      "used-text-color",
      "used-text-size",
      "used-text-weight",
      "used-text-opacity",
      "used-amount-icon",
      "used-amount-icon-color",
      "used-amount-icon-size",
      "total-text-color",
      "total-text-size",
      "total-text-weight",
      "total-text-opacity",
      "label-used",
      "label-out-of",
      "label-renews",
      "label-expires",
      "label-unlimited",
      "label-not-started",
    ];
  }

  // Format date as "23 May 2025" (long format)
  formatDateLong(isoString) {
    if (!isoString) return "";
    const date = new Date(isoString);
    const day = date.getDate();
    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  // Days until end date (ceiling)
  getDaysUntil(isoString) {
    if (!isoString) return 0;
    const end = new Date(isoString);
    const now = new Date();
    if (end <= now) return 0;
    const ms = end - now;
    return Math.ceil(ms / (24 * 60 * 60 * 1000));
  }

  attributeChangedCallback() {
    this.render();
  }

  // Helper: Format date as DD/MM/YYYY
  formatDate(isoString) {
    if (!isoString) return "";
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  // Helper: Format bytes to GB
  formatBytes(bytes) {
    if (!bytes && bytes !== 0) return "0";
    const gb = bytes / 1000000000;
    return gb.toFixed(2).replace(/\.?0+$/, ""); // Remove trailing zeros
  }

  // Helper: Check if date is in the past
  isExpired(isoString) {
    if (!isoString) return false;
    const endDate = new Date(isoString);
    const now = new Date();
    return endDate < now;
  }

  // Calculate percentage used
  getPercentage() {
    const unlimited = this.getAttribute("unlimited") === "true";
    const endDate = this.getAttribute("end-date") || "";

    // If expired, show as 100% used
    if (this.isExpired(endDate)) return 100;

    if (unlimited) return 0;

    const used = parseFloat(this.getAttribute("used-amount")) || 0;
    const total = parseFloat(this.getAttribute("total-amount")) || 1;
    return Math.min(100, (used / total) * 100);
  }

  render() {
    const planName = this.getAttribute("plan-name") || "";
    const usedAmount = this.getAttribute("used-amount") || "0";
    const totalAmount = this.getAttribute("total-amount") || "0";
    const unit = this.getAttribute("unit") || "GB";
    const unlimited = this.getAttribute("unlimited") === "true";
    const autoRenew = this.getAttribute("auto-renew") === "true";
    const endDate = this.getAttribute("end-date") || "";

    // Styling attributes
    const barFillColor = this.getAttribute("bar-fill-color") || "#5b4db3";
    const barFillGradientStart = this.getAttribute("bar-fill-gradient-start") || barFillColor;
    const barFillGradientEnd = this.getAttribute("bar-fill-gradient-end") || barFillColor;
    const barBgColor = this.getAttribute("bar-bg-color") || "#d9d9d9";
    const barHeight = this.getAttribute("bar-height") || "10";
    const barBorderRadius = this.getAttribute("bar-border-radius") || "5";

    // Card styling attributes
    const cardBgColor = this.getAttribute("card-bg-color") || "#FFFFFF";
    const cardBgTransparent = this.getAttribute("card-bg-transparent") === "true";
    const cardPadding = this.getAttribute("card-padding") || "16";
    const cardBorderRadius = this.getAttribute("card-border-radius") || "8";
    const cardBorderWidth = this.getAttribute("card-border-width") || "0";
    const cardBorderType = this.getAttribute("card-border-type") || "solid";
    const cardBorderColor = this.getAttribute("card-border-color") || "#E0E0E0";
    const cardBorderGradientStart = this.getAttribute("card-border-gradient-start") || "#5b4db3";
    const cardBorderGradientEnd = this.getAttribute("card-border-gradient-end") || "#8b7dd8";
    const cardBorderGradientAngle = this.getAttribute("card-border-gradient-angle") || "135";

    // Text styling attributes
    const titleTextColor = this.getAttribute("title-text-color") || "#000000";
    const titleTextSize = this.getAttribute("title-text-size") || "14";
    const titleTextWeight = this.getAttribute("title-text-weight") || "600";
    const titleTextOpacity = (this.getAttribute("title-text-opacity") || "100") / 100;
    const expiryTextColor = this.getAttribute("expiry-text-color") || "#000000";
    const expiryTextSize = this.getAttribute("expiry-text-size") || "12";
    const expiryTextWeight = this.getAttribute("expiry-text-weight") || "400";
    const expiryTextOpacity = (this.getAttribute("expiry-text-opacity") || "100") / 100;
    const usedTextColor = this.getAttribute("used-text-color") || "#000000";
    const usedTextSize = this.getAttribute("used-text-size") || "12";
    const usedTextWeight = this.getAttribute("used-text-weight") || "500";
    const usedTextOpacity = (this.getAttribute("used-text-opacity") || "100") / 100;
    const usedAmountIcon = this.getAttribute("used-amount-icon") || "";
    const usedAmountIconColor = this.getAttribute("used-amount-icon-color") || "#000000";
    const usedAmountIconSize = this.getAttribute("used-amount-icon-size") || "16";
    const totalTextColor = this.getAttribute("total-text-color") || "#000000";
    const totalTextSize = this.getAttribute("total-text-size") || "12";
    const totalTextWeight = this.getAttribute("total-text-weight") || "400";
    const totalTextOpacity = (this.getAttribute("total-text-opacity") || "100") / 100;

    // Label attributes
    const labelUsed = this.getAttribute("label-used") || "Used";
    const labelOutOf = this.getAttribute("label-out-of") || "out of";
    const labelRenews = this.getAttribute("label-renews") || "Renews";
    const labelExpires = this.getAttribute("label-expires") || "Expires";
    const labelUnlimited = this.getAttribute("label-unlimited") || "Unlimited data";
    const labelNotStarted = this.getAttribute("label-not-started") || "Not Started";

    const percentage = this.getPercentage();
    const formattedDate = this.formatDate(endDate);
    const formattedDateLong = this.formatDateLong(endDate);
    const bundleStarted = this.getAttribute("bundle-started") === "true";
    const startDate = this.getAttribute("start-date") || "";
    const formattedStartDateLong = startDate ? this.formatDateLong(startDate) : "";
    const renewsPosition = this.getAttribute("renews-position") || "header";
    const dateFormat = this.getAttribute("date-format") || "short";
    const showAllowanceLine = this.getAttribute("show-allowance-line") === "true";
    const allowanceLineColor = this.getAttribute("allowance-line-color") || "#000000";
    const allowanceLineFontWeight = this.getAttribute("allowance-line-font-weight") || "400";
    const labelStarted = this.getAttribute("label-started") || "Started";
    const labelOn = this.getAttribute("label-on") || "on";
    const labelAllowanceResetsIn = this.getAttribute("label-allowance-resets-in") || "Allowance resets in";
    const labelAllowanceExpiresIn = this.getAttribute("label-allowance-expires-in") || "Allowance expires in";
    const labelDays = this.getAttribute("label-days") || "days";

    // Build date text: short "Renews 23/05/25" | long "Started 23 May 2024, renews on 23 May 2025"
    let dateLabel;
    let dateText = "";
    if (!bundleStarted) {
      dateLabel = labelNotStarted;
      dateText = labelNotStarted;
    } else {
      dateLabel = autoRenew ? labelRenews : labelExpires;
      if (dateFormat === "long") {
        if (formattedStartDateLong && formattedDateLong) {
          dateText = `${labelStarted} ${formattedStartDateLong}, ${dateLabel.toLowerCase()} ${labelOn} ${formattedDateLong}`;
        } else if (formattedDateLong) {
          dateText = `${dateLabel} ${labelOn} ${formattedDateLong}`;
        } else {
          dateText = "";
        }
      } else {
        dateText = formattedDate ? `${dateLabel} ${formattedDate}` : "";
      }
    }

    const showDateInHeader = renewsPosition === "header" && dateText;
    const showDateBelowFooter = renewsPosition === "below_footer" && dateText;

    const daysUntil = this.getDaysUntil(endDate);
    const showAllowanceLineRow = showAllowanceLine && bundleStarted && !this.isExpired(endDate) && endDate;
    const allowanceLineText = showAllowanceLineRow
      ? (autoRenew ? `${labelAllowanceResetsIn} ${daysUntil} ${labelDays}` : `${labelAllowanceExpiresIn} ${daysUntil} ${labelDays}`)
      : "";

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          box-sizing: border-box;
        }

        .usage-bar-container {
          box-sizing: border-box;
          background-color: ${cardBgTransparent ? "transparent" : cardBgColor};
          padding: ${cardPadding}px;
          border-radius: ${cardBorderRadius}px;
          ${
            cardBorderWidth > 0 && cardBorderType === "solid"
              ? `
            border: ${cardBorderWidth}px solid ${cardBorderColor};
          `
              : ""
          }
          ${
            cardBorderWidth > 0 && cardBorderType === "gradient"
              ? `
            border: ${cardBorderWidth}px solid transparent;
            background-image:
              linear-gradient(${cardBgTransparent ? "transparent" : cardBgColor}, ${
                  cardBgTransparent ? "transparent" : cardBgColor
                }),
              linear-gradient(${cardBorderGradientAngle}deg, ${cardBorderGradientStart}, ${cardBorderGradientEnd});
            background-origin: padding-box, border-box;
            background-clip: padding-box, border-box;
          `
              : ""
          }
        }

        .usage-bar-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        @media screen and (max-width: 749px) {
            .usage-bar-header {
              flex-direction: column;
              align-items: flex-start;
              gap: 0.4rem;
            }
        }

        .usage-bar-plan-name {
          font-size: ${titleTextSize}px;
          color: ${titleTextColor};
          font-weight: ${titleTextWeight};
          opacity: ${titleTextOpacity};
        }

        .usage-bar-date {
          font-size: ${expiryTextSize}px;
          color: ${expiryTextColor};
          font-weight: ${expiryTextWeight};
          opacity: ${expiryTextOpacity};
        }

        .progress-bar-wrapper {
          width: 100%;
          height: ${barHeight}px;
          background-color: ${barBgColor};
          border-radius: ${barBorderRadius}px;
          overflow: hidden;
          margin-bottom: 8px;
        }

        .progress-bar-fill {
          height: 100%;
          width: ${unlimited ? "100" : percentage}%;
          background-image: linear-gradient(to right, ${barFillGradientStart}, ${barFillGradientEnd});
          border-radius: ${barBorderRadius}px;
          transition: width 0.3s ease;
          position: relative;
        }

        .usage-bar-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .usage-bar-used {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: ${usedTextSize}px;
          color: ${usedTextColor};
          font-weight: ${usedTextWeight};
          opacity: ${usedTextOpacity};
        }

        .usage-bar-used-icon {
          font-family: 'Material Symbols Outlined';
          font-size: ${usedAmountIconSize}px;
          color: ${usedAmountIconColor};
          font-weight: normal;
          font-style: normal;
          line-height: 1;
          letter-spacing: normal;
          text-transform: none;
          display: inline-block;
          white-space: nowrap;
          word-wrap: normal;
          direction: ltr;
          -webkit-font-feature-settings: 'liga';
          -webkit-font-smoothing: antialiased;
        }

        .progress-bar-fill-unlimited {
          color: ${barFillGradientStart};
          background: ${cardBgColor};
          font-family: 'Material Symbols Outlined';
          font-size: ${barHeight * 2}px;
          font-weight: normal;
          font-style: normal;
          line-height: ${barHeight}px;
          letter-spacing: normal;
          text-transform: none;
          text-align: center;
        }

        .progress-bar-unlimited-fade-before {
          position: absolute;
          left: 0;
          top: 0;
          width: 50%;
          height: 100%;
          background: linear-gradient(to right, ${barFillGradientStart}, transparent);
        }

        .progress-bar-unlimited-fade-after {
          position: absolute;
          right: 0;
          top: 0;
          width: 50%;
          height: 100%;
          background: linear-gradient(to left, ${barFillGradientEnd}, transparent);
        }

        .usage-bar-total {
          font-size: ${totalTextSize}px;
          color: ${totalTextColor};
          font-weight: ${totalTextWeight};
          opacity: ${totalTextOpacity};
        }
        .usage-bar-date-row {
          margin-top: 8px;
          font-size: ${expiryTextSize}px;
          color: ${expiryTextColor};
          font-weight: ${expiryTextWeight};
          opacity: ${expiryTextOpacity};
        }
        .usage-bar-allowance-line {
          margin-top: 6px;
          font-size: ${expiryTextSize}px;
          color: ${allowanceLineColor};
          font-weight: ${allowanceLineFontWeight};
          opacity: ${Number(expiryTextOpacity) * 0.9};
        }
      </style>

      <div class="usage-bar-container">
        <div class="usage-bar-header">
          <div class="usage-bar-plan-name">${planName}</div>
          ${showDateInHeader ? `<div class="usage-bar-date">${dateText}</div>` : ""}
        </div>

        <div class="progress-bar-wrapper">
          <div class="progress-bar-fill">
            ${
              unlimited
                ? `<div class="progress-bar-unlimited-fade-before"></div><div class="progress-bar-fill-unlimited material-symbols-outlined">all_inclusive</div><div class="progress-bar-unlimited-fade-after"></div>`
                : ""
            }
          </div>
        </div>

        <div class="usage-bar-footer">
          <div class="usage-bar-used">
            ${usedAmountIcon ? `<span class="usage-bar-used-icon">${usedAmountIcon}</span>` : ""}
            <span>${unlimited ? labelUnlimited : `${usedAmount} ${unit} ${labelUsed}`}</span>
          </div>
          ${unlimited ? "" : `<div class="usage-bar-total">${labelOutOf} ${totalAmount} ${unit}</div>`}
        </div>
        ${showDateBelowFooter ? `<div class="usage-bar-date-row">${dateText}</div>` : ""}
        ${showAllowanceLineRow ? `<div class="usage-bar-allowance-line">${allowanceLineText}</div>` : ""}
      </div>
    `;
  }
}

// Register the custom element
customElements.define("usage-bar", UsageBar);
